/**
 * CoConnect — Delivery Confirmation Modal (Enterprise Grade)
 *
 * Full delivery confirmation flow:
 *   1. Verify weight (from scale or manual entry)
 *   2. Check quality grade (A/B/C)
 *   3. Record moisture content
 *   4. Record spoilage
 *   5. Upload delivery photo
 *   6. Add notes
 *   7. Confirm delivery
 *
 * On submit:
 *   - Creates delivery_confirmation record
 *   - Updates dispatch status to DELIVERED
 *   - Updates batch with actual_weight, quality_grade, etc.
 *   - Sets truck status to AVAILABLE
 */
import React, { useState } from 'react';
import { apiClient } from '../services/api';
import {
  CheckCircle, X, Weight, Award, Droplets, AlertTriangle,
  Camera, FileText, Loader2, MapPin, User,
} from 'lucide-react';

interface DeliveryConfirmationProps {
  dispatch: {
    id: string;
    batch_id: string;
    truck_id: string;
    driver_id: string;
    batch_farmer_name: string;
    batch_farmer_phone: string;
    batch_farmer_village: string;
    batch_est_weight: number | null;
    batch_gps_lat: number | null;
    batch_gps_lng: number | null;
    truck_registration: string;
  };
  onClose: () => void;
  onConfirmed: () => void;
}

const DeliveryConfirmation: React.FC<DeliveryConfirmationProps> = ({
  dispatch,
  onClose,
  onConfirmed,
}) => {
  const [confirmedWeight, setConfirmedWeight] = useState(
    dispatch.batch_est_weight?.toString() || ''
  );
  const [qualityGrade, setQualityGrade] = useState<'A' | 'B' | 'C' | 'AUTO'>('AUTO');
  const [moistureContent, setMoistureContent] = useState('');
  const [spoilageKg, setSpoilageKg] = useState('0');
  const [photoUrl, setPhotoUrl] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [currentLat, setCurrentLat] = useState<number | null>(null);
  const [currentLng, setCurrentLng] = useState<number | null>(null);

  // Capture current GPS position on mount
  React.useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setCurrentLat(pos.coords.latitude);
          setCurrentLng(pos.coords.longitude);
        },
        () => {},
        { enableHighAccuracy: true, timeout: 10000 }
      );
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const weight = parseFloat(confirmedWeight);
    if (!weight || weight <= 0) {
      setError('Please enter a valid confirmed weight');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await apiClient.post(`/truck-portal/dispatches/${dispatch.id}/confirm`, {
        confirmedWeight: weight,
        qualityGrade: qualityGrade === 'AUTO' ? undefined : qualityGrade,
        moistureContent: moistureContent ? parseFloat(moistureContent) : undefined,
        spoilageKg: spoilageKg ? parseFloat(spoilageKg) : 0,
        photoUrl: photoUrl || undefined,
        notes: notes || undefined,
        arrivalLat: currentLat,
        arrivalLng: currentLng,
      });

      setSuccess(true);
      setTimeout(() => {
        onConfirmed();
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to confirm delivery');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
          <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-12 h-12 text-green-500" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Delivery Confirmed!</h2>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            Weight: <span className="font-bold">{confirmedWeight} kg</span> · Grade: <span className="font-bold">{qualityGrade === 'AUTO' ? 'Auto-calculated' : qualityGrade}</span>
          </p>
          <p className="text-sm text-gray-400">Updating dispatch and batch records...</p>
          <Loader2 className="w-6 h-6 text-orange-500 animate-spin mx-auto mt-4" />
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 overflow-y-auto">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-green-600 to-emerald-600 dark:from-gray-800 dark:to-gray-900 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Confirm Delivery</h2>
              <p className="text-sm text-green-100">Dispatch #{dispatch.id.slice(0, 8)}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white p-2">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Farmer info bar */}
        <div className="bg-gray-50 dark:bg-gray-900/50 px-6 py-3 border-b border-gray-200 dark:border-gray-700">
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div className="flex items-center space-x-2">
              <User className="w-4 h-4 text-gray-400" />
              <span className="text-gray-600 dark:text-gray-300">{dispatch.batch_farmer_name}</span>
            </div>
            <div className="flex items-center space-x-2">
              <MapPin className="w-4 h-4 text-gray-400" />
              <span className="text-gray-600 dark:text-gray-300">{dispatch.batch_farmer_village}</span>
            </div>
            <div className="flex items-center space-x-2">
              <Weight className="w-4 h-4 text-gray-400" />
              <span className="text-gray-600 dark:text-gray-300">
                Est: {dispatch.batch_est_weight || '?'} kg
              </span>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start text-red-700 dark:text-red-400 text-sm">
              <AlertTriangle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
              {error}
            </div>
          )}

          {/* Weight */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              <Weight className="w-4 h-4 inline mr-1" />
              Confirmed Weight (kg) *
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={confirmedWeight}
              onChange={(e) => setConfirmedWeight(e.target.value)}
              required
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
              placeholder="Enter actual weight from scale"
              autoFocus
            />
            <p className="text-xs text-gray-400 mt-1">
              Enter the weight shown on the digital scale at intake.
            </p>
          </div>

          {/* Quality grade */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              <Award className="w-4 h-4 inline mr-1" />
              Quality Grade
            </label>
            <div className="grid grid-cols-4 gap-2">
              <button
                key="AUTO"
                type="button"
                onClick={() => setQualityGrade('AUTO')}
                className={`py-3 rounded-lg font-bold text-sm transition-all ${
                  qualityGrade === 'AUTO'
                    ? 'bg-blue-500 text-white shadow-lg'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                Auto
              </button>
              {(['A', 'B', 'C'] as const).map(grade => (
                <button
                  key={grade}
                  type="button"
                  onClick={() => setQualityGrade(grade)}
                  className={`py-3 rounded-lg font-bold text-lg transition-all ${
                    qualityGrade === grade
                      ? grade === 'A' ? 'bg-green-500 text-white shadow-lg'
                        : grade === 'B' ? 'bg-yellow-500 text-white shadow-lg'
                        : 'bg-red-500 text-white shadow-lg'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {grade}
                </button>
              ))}
            </div>
            {qualityGrade === 'AUTO' && (
              <p className="text-xs text-blue-500 mt-1.5">
                System will auto-calculate grade from moisture, spoilage, and weight accuracy.
              </p>
            )}
          </div>

          {/* Moisture + Spoilage */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                <Droplets className="w-4 h-4 inline mr-1" />
                Moisture Content (%)
              </label>
              <input
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={moistureContent}
                onChange={(e) => setMoistureContent(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="e.g., 7.5"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                <AlertTriangle className="w-4 h-4 inline mr-1" />
                Spoilage (kg)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={spoilageKg}
                onChange={(e) => setSpoilageKg(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="0"
              />
            </div>
          </div>

          {/* Photo URL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              <Camera className="w-4 h-4 inline mr-1" />
              Delivery Photo URL (optional)
            </label>
            <input
              type="url"
              value={photoUrl}
              onChange={(e) => setPhotoUrl(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
              placeholder="https://supabase.co/bucket/delivery-photo.jpg"
            />
            <p className="text-xs text-gray-400 mt-1">
              Upload a photo of the delivered batch and paste the URL here.
            </p>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              <FileText className="w-4 h-4 inline mr-1" />
              Delivery Notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
              placeholder="Any observations about the delivery, road conditions, farmer interaction, etc."
            />
          </div>

          {/* GPS indicator */}
          {currentLat && currentLng && (
            <div className="flex items-center text-xs text-gray-400">
              <MapPin className="w-3 h-3 mr-1" />
              GPS captured: {currentLat.toFixed(5)}, {currentLng.toFixed(5)}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg font-bold hover:from-green-700 hover:to-emerald-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  Confirming...
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Confirm Delivery
                </>
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DeliveryConfirmation;
